import { Box, TextField, Typography } from "@mui/material";
import React, { useCallback, useContext } from "react";
import { IFailureDetailsSelectionsProps } from "./FailureDetailsSelectionsInterfaces";
import { FailureDetailsSelection } from "../../../../../../components/FailureDetailsSelection/FailureDetailsSelection.view";
import { HMIContext } from "../../../../../../contexts/HMIContext/HMIContext";
import { HMICachedDataContext } from "../../../../../../contexts/HMICachedDataContext/HMICachedDataContext";
import { useTranslation } from "react-i18next";
import { useStyles } from "./FailureDetailsSelections.styles";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import {
    DefaultTranslationFieldsInTable,
    FAILUREDETAILSSTEPS,
    NOTES_MAX_INPUT_HMI,
} from "../../../../../../constants/constants";
import { DowntimeReportCreationDetails } from "./DowntimeReportCreationDetails/DowntimeReportCreationDetails.view";
import { RemainingCharacterCounter } from "components/RemainingCharacterCounter/RemainingCharacterCounter.view";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";

export const FailureDetailsSelections = ({
    failureType,
    area,
    functionalLocation,
    failureCode,
}: IFailureDetailsSelectionsProps) => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const { handleNotes, notes } = useContext(HMIContext);
    const { store } = useContext(HMICachedDataContext);

    const handleOnChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            event.preventDefault();
            handleNotes(event.target.value);
        },
        [handleNotes],
    );

    const defaultFailureTypeTranslatedName = translateDefaultEntity(
        i18n.language,
        failureType,
        "name",
        DefaultTranslationFieldsInTable.FAILURE_TYPE,
    );
    const defaultFailureCodeTranslatedName = translateDefaultEntity(
        i18n.language,
        failureCode,
        "name",
        DefaultTranslationFieldsInTable.FAILURE_CODE,
    );

    return (
        <>
            {store.isEventReportEditing && (
                <DowntimeReportCreationDetails
                    timeStop={store.editEventReportData.row!.end_date!}
                    timeStart={store.editEventReportData.row!.start_date ?? ""}
                    duration={store.editEventReportData.row!.duration}
                    operator={`${store.editEventReportData.row!.user_operator.first_name} ${
                        store.editEventReportData.row!.user_operator.last_name
                    }`}
                />
            )}
            <Box className={classes.failureDetailsSelectionsContainer}>
                <FailureDetailsSelection
                    title={t("HMI.failure_details.selections.failure_type_title")}
                    name={defaultFailureTypeTranslatedName}
                    failureDetailStep={FAILUREDETAILSSTEPS.FAILURETYPES}
                    color={failureType!.color_code}
                />
                {store.isEventReportEditing ? (
                    <>
                        {failureType!.is_location_specific && (
                            <>
                                <ArrowForwardOutlinedIcon className={classes.arrowIcon} />
                                <FailureDetailsSelection
                                    title={t("HMI.failure_details.selections.area_title")}
                                    name={
                                        area
                                            ? handleNameTranslation(i18n.language, area.translation.translation_names)
                                            : t("HMI.details.edit_event_report.not_selected")
                                    }
                                    failureDetailStep={FAILUREDETAILSSTEPS.AREAS}
                                />
                                {area && (
                                    <>
                                        <ArrowForwardOutlinedIcon className={classes.arrowIcon} />
                                        <FailureDetailsSelection
                                            title={t("HMI.failure_details.selections.functional_location_title")}
                                            name={
                                                functionalLocation
                                                    ? handleNameTranslation(
                                                          i18n.language,
                                                          functionalLocation.translation.translation_names,
                                                      )
                                                    : t("HMI.details.edit_event_report.not_selected")
                                            }
                                            failureDetailStep={FAILUREDETAILSSTEPS.FUNCTIONALLOCATION}
                                        />
                                    </>
                                )}
                            </>
                        )}
                        {Boolean(failureType?.failure_codes?.length) &&
                            (failureType!.is_location_specific ? functionalLocation : true) && (
                                <>
                                    <ArrowForwardOutlinedIcon className={classes.arrowIcon} />
                                    <FailureDetailsSelection
                                        title={t("HMI.failure_details.selections.failure_code_title")}
                                        name={
                                            defaultFailureCodeTranslatedName ??
                                            t("HMI.details.edit_event_report.not_selected")
                                        }
                                        failureDetailStep={FAILUREDETAILSSTEPS.FAILURECODES}
                                    />
                                </>
                            )}
                    </>
                ) : (
                    <>
                        {failureType!.is_location_specific && area && functionalLocation && (
                            <>
                                <ArrowForwardOutlinedIcon className={classes.arrowIcon} />
                                <FailureDetailsSelection
                                    title={t("HMI.failure_details.selections.area_title")}
                                    name={handleNameTranslation(i18n.language, area.translation.translation_names)}
                                    failureDetailStep={FAILUREDETAILSSTEPS.AREAS}
                                />
                                <ArrowForwardOutlinedIcon className={classes.arrowIcon} />
                                <FailureDetailsSelection
                                    title={t("HMI.failure_details.selections.functional_location_title")}
                                    name={handleNameTranslation(
                                        i18n.language,
                                        functionalLocation.translation.translation_names,
                                    )}
                                    failureDetailStep={FAILUREDETAILSSTEPS.FUNCTIONALLOCATION}
                                />
                            </>
                        )}
                        <ArrowForwardOutlinedIcon className={classes.arrowIcon} />
                        <FailureDetailsSelection
                            title={t("HMI.failure_details.selections.failure_code_title")}
                            name={defaultFailureCodeTranslatedName ?? t("HMI.details.edit_event_report.not_selected")}
                            failureDetailStep={FAILUREDETAILSSTEPS.FAILURECODES}
                        />
                    </>
                )}
            </Box>
            <Box className={classes.notesContainer}>
                <Typography variant="subtitle2" className={classes.notesTitle}>
                    {t("HMI.failure_details.selections.notes").toUpperCase()}
                </Typography>
                <TextField
                    className={classes.notesField}
                    multiline
                    fullWidth
                    value={notes}
                    onChange={handleOnChange}
                    inputProps={{ maxLength: NOTES_MAX_INPUT_HMI }}
                />
                <RemainingCharacterCounter maxLength={NOTES_MAX_INPUT_HMI} value={notes} />
            </Box>
        </>
    );
};
